import { Link } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Button from '../../../common/Button';
import Loading from '../../../common/Loading';
import CustomTable from '../../../common/table/CustomTable';
import StatusChip, { StatusConfig } from '../../../common/StatusChip';
import { ApplicantData, ApplicantResponse, Column } from '../../../common/table/types';
import api from '../../../../utils/api';
import { formatDate } from '../../../../utils/date';

interface FetchApplicantsParams {
  status: string | null;
  source: string | null;
  vacancyCode: string;
  vacancyOrName: string;
  page: number;
  limit: number;
  sort?: string;
}

interface ApplicantTableProps {
  status: string | null;
  source: string | null;
  vacancyCode: string;
  vacancyOrName: string;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

const statusConfig: StatusConfig = {
  'Open CV': {
    label: 'Open CV',
    color: '#1B8E3D',
    backgroundColor: '#E8F5E9'
  },
  'Shortlisted': {
    label: 'Shortlisted',
    color: '#B54708',
    backgroundColor: '#FFFAEB'
  },
  'Rejected': {
    label: 'Rejected',
    color: '#B42318',
    backgroundColor: '#FEF3F2'
  },
  'Joined': {
    label: 'Joined',
    color: '#1570EF',
    backgroundColor: '#EFF8FF'
  },
  'Active': {
    label: 'Active',
    color: '#1B8E3D',
    backgroundColor: '#E8F5E9'
  },
  'Inactive': {
    label: 'Inactive',
    color: '#B42318',
    backgroundColor: '#FEF3F2'
  }
};

const ApplicantTable: React.FC<ApplicantTableProps> = ({
  status,
  source,
  vacancyCode,
  vacancyOrName,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange
}) => {
  const [sortField, setSortField] = useState<keyof ApplicantData | undefined>("createdAt");
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [applicants, setApplicants] = useState<ApplicantData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchApplicants = async (params: FetchApplicantsParams) => {
    try {
      setLoading(true);
      const response = await api.get<ApplicantResponse>(
        '/rec/applicant/search',
        {
          params: {
            ...params,
            sort: sortField ? `${sortField}:${sortDirection}` : undefined
          },
          headers: {
            'x-api-id': 1,
            'x-page-id': 1,
            'x-token': 'PSBrwdyDEe4tP8E1X89lEjeD3e7IpebrFtypPjwFD9S7baxiRhIxP5dyLJuqrxV0',
          }
        }
      );
      setApplicants(response.data.data);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Error fetching applicants:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplicants({
      status,
      source,
      vacancyCode,
      vacancyOrName,
      page: page + 1, // API uses 1-based pagination
      limit: rowsPerPage
    });
  }, [page, rowsPerPage, status, source, vacancyCode, vacancyOrName, sortField, sortDirection]);

  const handleSort = (field: keyof ApplicantData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const columns: Column<ApplicantData>[] = [
    {
      field: 'firstName',
      headerName: 'Name',
      sortable: true,
      width: '300px',
      type: 'text',
      renderCell: (row) => `${row.firstName || ''} ${row.lastName || ''}`
    },
    {
      field: 'vacancy',
      headerName: 'Vacancy',
      sortable: true,
      width: '320px',
      type: 'text'
    },
    {
      field: 'vacancyCode',
      headerName: 'Code',
      sortable: true,
      width: '100px',
      type: 'text'
    },
    {
      field: 'source',
      headerName: 'Source',
      sortable: true,
      width: '120px',
      type: 'text'
    },
    {
      field: 'issueId',
      headerName: 'JRA Issue ID',
      sortable: true,
      width: '120px',
      type: 'text',
      renderCell: (row) => row.issueId ? <Link href={`https://eyepax.atlassian.net/browse/${row.issueId}`} target="_blank" rel="noopener noreferrer">{row.issueId}</Link> : '-',
    },
    {
      field: 'currentStatus',
      headerName: 'Status',
      sortable: true,
      width: '80px',
      type: 'status',
      renderCell: (row) => <StatusChip status={row.currentStatus} statusConfig={statusConfig} />,
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      sortable: true,
      width: '40x',
      type: 'date',
      align: 'center',
      renderCell: (row) => formatDate(row.createdAt),
    },
    {
      field: 'actions',
      headerName: '',
      width: '150px',
      sortable: false,
      type: 'text',
      renderCell: () => (
        <Button
          variant="contained"
          width="120px"
          style={{ height: '27px', fontSize: '12px' }}
          onClick={() => setOpen(true)}
        >
          Generate CV
        </Button>
      ),
    },
  ];

  return (
    <div>
      {loading ? (
        <Loading message="Loading applicants..." />
      ) : (
        <CustomTable<ApplicantData>
          columns={columns}
          rows={applicants}
          onSort={handleSort}
          sortField={sortField}
          sortDirection={sortDirection}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </div>
  );
};

export default ApplicantTable; 